import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMe } from '../lib/data';
import { useDragDrop } from './dnd';
import { useSearchParams } from 'react-router-dom';
import { api, trpc, upsertCatalogExercise, useCatalog } from '@tg/app-core';
import { cloneContent, dayCount, dayHas, emptyWorkout, mkBlock, mkLine, type Block, type Line, type WorkoutContent } from '@tg/domain';
import { RopeAnim } from './AiWait';
import { ConstructorCtx, isMac, type Ctx } from './ctx';
import { DAYICON, ICON } from './icons';
import { Kebab } from './Kebab';
import { Svg } from './Svg';
import { BlockView } from './BlockView';
import { LineView } from './Lines';
import { Rail } from './Rail';
import { MRailBackdrop, MRailButton, useMobileRail } from '../shell/Mobile';
import * as M from './model';
import { addDays, dm, longDate, monthEnd, monthStart, today } from './dates';
import { WhoPicker, type WhoItem } from './WhoPicker';
import { usePagesCss } from '../styles';
import { useTitleSetter } from '../shell/Shell';
import { DatePick } from './DatePick';
import { GroupConflicts, type Conflict, type Decisions } from './GroupConflicts';
import { InviteLink } from '../ui/InviteLink';
import { LimitAlert } from '../ui/LimitAlert';
import { isOnline } from '../offline/net';
import { onReload, pendingOps, run as runOp, seenRev } from '../offline/outbox';

export type Who = { clientId: string } | { groupId: string };
const SAVE_KEY = isMac ? '⌘S' : 'Ctrl+S';
const whoKey = (w: Who) => ('clientId' in w ? 'c:' + w.clientId : 'g:' + w.groupId);

/* Пустой день открывается с одной пустой строкой — сразу можно печатать (быстрый старт) */
const freshDoc = (): WorkoutContent => ({ title: '', blocks: [mkBlock({ items: [mkLine(null, '')] })] });
const isFresh = (d: WorkoutContent) => !d.comp && !d.message && !dayHas(d) && d.blocks.every((b) => M.isBlank(b) && typeof b.text !== 'string');

/** Тост с отменой (как toast в trainer.js) */
function useToast() {
  const [t, setT] = useState<{ text: string; undo?: () => void; id: number } | null>(null);
  const timer = useRef<number>(0);
  const show = useCallback((text: string, undo?: () => void) => {
    clearTimeout(timer.current);
    setT({ text, undo, id: Date.now() });
    timer.current = window.setTimeout(() => setT(null), 5000);
  }, []);
  const el = t ? (
    <div className="toast on" key={t.id}>
      <span>{t.text}</span>
      {t.undo && (
        <button
          className="undo"
          onClick={() => {
            t.undo!();
            setT(null);
          }}
        >
          Отменить
        </button>
      )}
    </div>
  ) : null;
  return [show, el] as const;
}

export function Constructor() {
  const setTitle = useTitleSetter();
  /* pages.css на конструкторе не подключён — как в прототипе */
  usePagesCss(false);
  const [sp, setSp] = useSearchParams();
  const catalog = useCatalog();
  const utils = trpc.useUtils();
  /* Замороженным клиентам тренировки не составляются — в конструкторе их нет */
  const clients = trpc.trainer.clients.useQuery(undefined, { select: (cs) => cs.filter((c) => !c.frozenAt) });
  const groups = trpc.trainer.groups.useQuery();
  const me = trpc.trainer.me.useQuery();
  const [toast, toastEl] = useToast();

  /* Кто и какой день */
  const who: Who | null = sp.get('group') ? { groupId: sp.get('group')! } : sp.get('client') ? { clientId: sp.get('client')! } : null;
  const isGroup = !!who && 'groupId' in who;
  const [picker, setPicker] = useState<null | ((w: Who) => void)>(null);
  useEffect(() => {
    /* Без клиента — первый по списку */
    if (!who && clients.data?.length) setSp((p) => (p.set('client', clients.data![0].id), p), { replace: true });
  }, [who, clients.data]);

  const date = sp.get('date') || '';
  /* Другой день или человек — пустое поле сообщения, открытое на прежнем, не тянется за ним */
  useEffect(() => {
    if (msgOpen.open) msgOpen.set(false);
  }, [date, sp.get('client'), sp.get('group')]);
  const range = useMemo(() => (date ? { from: monthStart(addDays(date, -7)), to: monthEnd(addDays(date, 21)) } : null), [date]);
  /* refetchOnMount 'always': документ дня ждёт ответа на этот запрос (ниже), даже если на устройстве свежий кэш */
  const days = trpc.trainer.days.useQuery({ who: who!, from: range?.from ?? today(), to: range?.to ?? today() }, { enabled: !!who && !!range, refetchOnMount: 'always' });
  /* Дата не задана — первый ещё не составленный день с сегодняшнего (как «Создать тренировку») */
  const ahead = trpc.trainer.days.useQuery({ who: who!, from: today(), to: addDays(today(), 120) }, { enabled: !!who && !date });
  useEffect(() => {
    if (!who || date || !ahead.data) return;
    const has = new Set(ahead.data.filter((d) => dayHas(d.content)).map((d) => d.date));
    let d = today();
    while (has.has(d)) d = addDays(d, 1);
    setSp((p) => (p.set('date', d), p), { replace: true });
  }, [ahead.data, date, who]);

  const maxes = trpc.trainer.maxesFor.useQuery(who!, { enabled: !!who });
  const pm = useMemo(() => (who && 'clientId' in who ? maxes.data?.[who.clientId] || {} : {}), [maxes.data, who]);
  const row = days.data?.find((d) => d.date === date) ?? null;

  /* Документ дня: несохранённые правки (work) поверх сохранённой версии */
  const [doc, setDoc] = useState<WorkoutContent | null>(null);
  const loadedFor = useRef('');
  /* Конфликт оффлайна решён «с сервера» — документ дня перечитывается из свежего ответа сервера */
  const [reloadTick, setReloadTick] = useState(0);
  const refetchDays = useRef(days.refetch);
  refetchDays.current = days.refetch;
  useEffect(
    () =>
      onReload(async (key) => {
        if (key !== loadedFor.current) return;
        await refetchDays.current();
        loadedFor.current = '';
        setReloadTick((n) => n + 1);
      }),
    [],
  );
  useEffect(() => {
    if (!who || !date || !days.data) return;
    /* Документ — только из ответа на запрос дня, не из кэша прошлого захода: со связью это свежие данные сервера,
       без связи — данные устройства с правками из очереди (offline/link) */
    if (!days.isFetchedAfterMount) return;
    const k = whoKey(who) + '@' + date;
    if (loadedFor.current === k) return;
    loadedFor.current = k;
    /* Версия, от которой тренер правит, — для правок без сети */
    seenRev(k, row?.rev ?? 0);
    const src = row?.work ?? row?.content;
    setDoc(src && (dayHas(src) || src.message || src.comp || src.title) ? (JSON.parse(JSON.stringify(src)) as WorkoutContent) : freshDoc());
  }, [days.data, days.isFetchedAfterMount, who, date, reloadTick]);

  /* Переход из ленты по вопросу (?ref=блок или строка): прокрутить к блоку и подсветить его */
  const ref = sp.get('ref');
  const shownRef = useRef('');
  useEffect(() => {
    if (!ref || !doc || shownRef.current === ref + '@' + date) return;
    const b = doc.blocks.find((x) => x.id === ref || (x.items || []).some((y) => y.id === ref || (y.t === 'set' && y.items.some((l) => l.id === ref))));
    if (!b) return;
    shownRef.current = ref + '@' + date;
    setTimeout(() => {
      const el = document.querySelector(`[data-blk="${b.id}"]`);
      if (!el) return;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el.classList.add('qflash');
      setTimeout(() => el.classList.remove('qflash'), 2600);
    }, 150);
  }, [ref, doc, date]);

  /* Автосохранение несохранённых правок (CON-20): пишутся сразу, в календарь — по «Сохранить» */
  const baseSig = M.signature(row?.work ?? row?.content ?? emptyWorkout());
  const [pending, setPending] = useState(false);
  const saveTimer = useRef<number>(0);
  const flush = useRef<() => Promise<void>>(async () => {});
  const dirtyLocal = !!doc && M.signature(doc) !== baseSig;
  useEffect(() => {
    if (!doc || !who || !date) return;
    const sig = M.signature(doc);
    /* Несохранённых правок нет (в т. ч. день перечитан с сервера) — «Сохранить» не досылает прежний документ */
    if (sig === baseSig) {
      flush.current = async () => {};
      return;
    }
    setPending(true);
    const run = async () => {
      clearTimeout(saveTimer.current);
      /* Без сети — в очередь на устройстве (offline/outbox), иначе сразу на сервер, как раньше */
      await runOp({ op: 'editDay', input: { who, date, content: M.cleanForSave(doc) } });
      await utils.trainer.days.invalidate();
      setPending(false);
    };
    flush.current = run;
    saveTimer.current = window.setTimeout(run, 700);
    return () => clearTimeout(saveTimer.current);
  }, [doc]);

  const update = useCallback((f: (d: WorkoutContent) => WorkoutContent) => setDoc((d) => (d ? f(d) : d)), []);
  /* Перетаскивание блоков и строк за ручку «⠿» */
  useDragDrop(update, (m) => toast(m));
  const [focus, setFocusS] = useState<{ id: string; field: string } | null>(null);
  const setFocus = useCallback((id: string, field = 'e') => setFocusS(id ? { id, field } : null), []);
  const unitSel = useRef<Record<string, string>>({}).current;

  const setMaxM = trpc.trainer.setMax.useMutation({ onSuccess: () => utils.trainer.maxesFor.invalidate() });
  const saveTpl = trpc.trainer.saveTemplate.useMutation();
  const saveEx = trpc.trainer.saveExercise.useMutation({ onSuccess: (x) => upsertCatalogExercise(utils, x) });

  /* Ширина колонки названий по самому длинному в блоке (alignNames) */
  const docRef = useRef<HTMLElement>(null);
  const [nmw, setNmw] = useState<Record<string, number>>({});
  useLayoutEffect(() => {
    const el = docRef.current;
    if (!el) return;
    el.classList.add('nm-measure');
    const out: Record<string, number> = {};
    /* Колонка названия не резиновая (flex:none;width:var(--nmw)) — на длинном названии и узком
       окне ей больше нечем поделиться с полями схемы/нагрузки справа, и те обрезаются краем блока
       (overflow:clip у .doc). Правило из прототипа капало только по самому названию (160–380px);
       добавляем верхнюю границу по реальной ширине блока, чтобы полям всегда оставалось место.
       Резерв под поля — не плоская константа, а то, сколько места реально просит самая длинная
       строка справа от названия (диапазон «85–90 % → 127,5–135 кг» ощутимо шире одиночного числа
       и раньше просто обрезался краем .doc, если плоского резерва на него не хватало). */
    const PARAMS_MIN = 230;
    el.querySelectorAll<HTMLElement>('.blk').forEach((bk) => {
      let w = 0;
      let paramsW = 0;
      bk.querySelectorAll<HTMLElement>('.line:not(.raw)').forEach((ln) => {
        const t = ln.querySelector<HTMLElement>(':scope > .txt');
        if (!t) return;
        w = Math.max(w, t.getBoundingClientRect().width + (t.closest('.ssg') ? 26 : 0));
        /* Только .prm (схема/нагрузка/подсказка веса) — .sp после него flex:1 и раздувает
           scrollWidth строки до полной ширины блока, не отражая реальную потребность в месте. */
        const prm = ln.querySelector<HTMLElement>(':scope > .prm');
        if (prm) paramsW = Math.max(paramsW, prm.getBoundingClientRect().width);
      });
      const reserve = Math.max(PARAMS_MIN, paramsW + 16);
      const cap = Math.max(160, Math.min(380, bk.clientWidth - reserve));
      out[bk.dataset.blk!] = Math.ceil(Math.min(Math.max(w, 160), cap));
    });
    el.classList.remove('nm-measure');
    if (JSON.stringify(out) !== JSON.stringify(nmw)) setNmw(out);
  });

  /* ─── Сохранение и публикация ─── */
  const [conflicts, setConflicts] = useState<{ list: Conflict[]; resolve: (d: Decisions | null) => void } | null>(null);
  /** Окно занятых дней группы (GRP-3) перед публикацией; null — отмена */
  const decide = async (publish: boolean): Promise<Decisions | null | undefined> => {
    if (!who || !('groupId' in who) || !publish) return undefined;
    const list = await utils.trainer.groupConflicts.fetch({ groupId: who.groupId, dates: [date] });
    if (!list.length) return undefined;
    return new Promise((resolve) => setConflicts({ list, resolve }));
  };
  /** Публикация в группу проверяет занятые дни участников на сервере — без сети её не сделать */
  const groupNeedsNet = (publish: boolean) => {
    if (!isGroup || !publish || isOnline()) return false;
    toast('Публикация в группу — когда появится интернет: нужно проверить дни участников');
    return true;
  };
  /** Правка ушла в очередь на устройстве — тренер видит это в подсказке */
  const queued = () => !isOnline() || pendingOps().length > 0;
  const save = async () => {
    if (!who) return;
    await flush.current();
    if (groupNeedsNet(row?.status === 'published')) return;
    const decisions = await decide(row?.status === 'published');
    if (decisions === null) return;
    await runOp({ op: 'saveDays', input: { who, dates: null, decisions } });
    await utils.trainer.days.invalidate();
    if (queued()) toast('Сохранено на устройстве — уйдёт на сервер, когда появится интернет');
    else toast('Сохранено' + (row?.status === 'published' ? ' — клиент видит новую версию' : ''));
  };
  const toggle = async () => {
    if (!who) return;
    await flush.current();
    const on = row?.status !== 'published';
    if (groupNeedsNet(on)) return;
    const decisions = await decide(on);
    if (decisions === null) return;
    await runOp({ op: 'setStatus', input: { who, date, published: on, decisions } });
    await utils.trainer.days.invalidate();
    if (queued()) toast(on ? 'Опубликуется, когда появится интернет — до этого ' + (isGroup ? 'участники' : 'клиент') + ' не видит' : 'Станет черновиком, когда появится интернет');
    else toast(on ? (isGroup ? 'Опубликована — участники видят' : 'Опубликована — клиент видит') : 'Черновик — ' + (isGroup ? 'участники не видят' : 'клиент не видит'));
  };
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        (document.activeElement as HTMLElement)?.blur?.();
        setTimeout(save, 50);
      }
    };
    document.addEventListener('keydown', k);
    return () => document.removeEventListener('keydown', k);
  });

  const clearDay = trpc.trainer.clearDay.useMutation();

  /* ─── Кнопка AI (CON-5): разбор на сервере моделью (OpenAI); нет связи с ИИ — правила workout-parser ─── */
  const [proposal, setProposal] = useState<{ ids: Set<string>; source: Block; at: number; logId?: string | null } | null>(null);
  const [aiBusy, setAiBusy] = useState<string | null>(null);
  const docNow = useRef(doc);
  docNow.current = doc;
  const aiText = trpc.trainer.aiText.useMutation();
  /* Принял тренер разбор или откатил — в журнал ИИ, чтобы видеть качество */
  const aiOutcome = trpc.trainer.aiOutcome.useMutation();
  const closeProposal = (outcome: 'accepted' | 'reverted') => {
    if (proposal?.logId) aiOutcome.mutate({ logId: proposal.logId, outcome });
    setProposal(null);
  };
  const aiBlock = async (id: string, typed?: string) => {
    if (!doc || aiBusy) return;
    const b0 = doc.blocks.find((x) => x.id === id);
    const text = (typed ?? b0?.text ?? '').trim();
    if (!b0 || !text) return;
    setAiBusy(id);
    let parsed: Block[];
    let logId: string | null = null;
    try {
      const r = await aiText.mutateAsync({ text });
      parsed = r.blocks as Block[];
      logId = r.logId;
    } catch (e) {
      parsed = catalog.parser.blocks(text);
      const msg = e instanceof Error && e.message && !/fetch|network/i.test(e.message) ? e.message : 'Нет связи с ИИ';
      if (parsed.length) toast(msg + ' — разобрал по простым правилам, проверьте');
      else return (setAiBusy(null), toast(msg));
    }
    setAiBusy(null);
    if (!parsed.length) return toast('Не нашёл, что разобрать — текст остался как есть');
    /* Пока ИИ думал, документ мог поменяться — берём блок из текущего состояния */
    const cur = docNow.current;
    const at = cur ? cur.blocks.findIndex((x) => x.id === id) : -1;
    if (!cur || at < 0) return;
    setProposal({ ids: new Set(parsed.map((x) => x.id)), source: { ...cur.blocks[at], text }, at, logId });
    update((d) => M.replaceBlock(d, id, parsed));
  };

  /* ─── Фото тренировки (AI-4): снимок тетради или скриншот → блок текстом, дальше — кнопка AI ─── */
  const [photoBusy, setPhotoBusy] = useState(false);
  const aiPhoto = trpc.trainer.aiPhoto.useMutation();
  const photoInput = useRef<HTMLInputElement>(null);
  const onPhoto = async (file: File | null | undefined) => {
    if (!file || photoBusy || !doc) return;
    if (!/^image\//.test(file.type)) return toast('Нужна картинка — фото или скриншот');
    setPhotoBusy(true);
    try {
      const image = await shrinkImage(file);
      const { text } = await aiPhoto.mutateAsync({ image });
      if (!text.trim()) return toast('На фото не нашёл тренировку');
      update((d) => (d.blocks.length === 1 && !dayHas(d) ? { ...d, blocks: [{ ...mkBlock(), text }] } : M.addTextBlockWith(d, text)));
      toast('Фото распознано — проверьте текст и нажмите AI, чтобы разбить на упражнения');
    } catch (e) {
      toast(e instanceof Error && e.message && !/fetch|network/i.test(e.message) ? e.message : 'Не удалось распознать фото — попробуйте ещё раз');
    } finally {
      setPhotoBusy(false);
    }
  };
  /* Скриншот из буфера (Ctrl/⌘+V) — в любом месте конструктора */
  const onPhotoRef = useRef(onPhoto);
  onPhotoRef.current = onPhoto;
  useEffect(() => {
    const h = (e: ClipboardEvent) => {
      const f = [...(e.clipboardData?.items || [])].find((i) => i.kind === 'file' && i.type.startsWith('image/'))?.getAsFile();
      if (!f) return;
      e.preventDefault();
      onPhotoRef.current(f);
    };
    document.addEventListener('paste', h);
    return () => document.removeEventListener('paste', h);
  }, []);
  const photoBtn = (
    <>
      <button className="addb" id="add-blk-photo" disabled={photoBusy} title="Фото тетради или скриншот — ИИ перепишет тренировку текстом. Можно просто вставить скриншот: Ctrl/⌘+V" onClick={() => photoInput.current?.click()}>
        {photoBusy ? <RopeAnim size={50} /> : <Svg src={ICON.photo} />} {photoBusy ? 'Распознаю фото…' : 'Фото'}
      </button>
      <input
        ref={photoInput}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          onPhoto(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </>
  );
  const aiLine = (id: string) => {
    if (!doc) return;
    const l = M.findLine(doc, id);
    if (!l || l.t !== 'ex' || !l.raw) return;
    const raw = l.raw;
    const set = catalog.parser.setHead(raw);
    let items: any[] | null = null;
    if (set && set.list.length) {
      const r = catalog.parser.blocks(raw);
      items = r[0]?.items ?? null;
    } else {
      const r = catalog.parser.line(raw);
      if (r.type === 'ok') items = [{ ...r.item, id }];
    }
    if (!items) return toast('ИИ не узнал строку уверенно — она осталась текстом');
    const before = doc;
    update((d) => M.replaceLine(d, id, items!));
    toast('Строка разобрана', () => update(() => before));
  };
  const proposalStat = () => {
    if (!proposal || !doc) return '';
    const bs = doc.blocks.filter((b) => proposal.ids.has(b.id));
    const lines = bs.flatMap((b) => b.items.flatMap((y) => (y.t === 'set' ? y.items : [y])));
    const fromBase = lines.filter((y) => (y.t === 'chain' ? y.parts.every((p) => p.exId) : !!y.exId)).length;
    const raw = lines.length - fromBase;
    return `${bs.length} ${bs.length === 1 ? 'блок' : bs.length < 5 ? 'блока' : 'блоков'} · ${fromBase} из базы${raw ? ` · ${raw} текстом` : ''}`;
  };

  const whoItem: WhoItem | null = useMemo(() => {
    if (!who) return null;
    if ('groupId' in who) {
      const g = groups.data?.find((x) => x.id === who.groupId);
      return g ? { kind: 'group', id: g.id, name: g.name, ini: ini(g.name), sub: `${g.members.length} ${g.members.length === 1 ? 'участник' : 'участников'}` } : null;
    }
    const c = clients.data?.find((x) => x.id === who.clientId);
    return c
      ? { kind: 'client', id: c.id, name: c.name, ini: ini(c.name), photo: c.photo, sub: [c.group?.name, c.composedTill ? 'составлено до ' + dm(c.composedTill) : 'ничего не составлено'].filter(Boolean).join(' · '), overLimit: c.overLimit }
      : null;
  }, [who, clients.data, groups.data]);

  /* Что записал клиент в этот день — под блоками (из ленты «Результат» сюда и ведёт) */
  const resQ = trpc.trainer.results.useQuery({ clientId: who && 'clientId' in who ? who.clientId : '', from: date, to: date }, { enabled: !!who && 'clientId' in who && !!date });

  const ctx: Ctx | null = doc
    ? {
        doc,
        update,
        catalog,
        pm,
        isGroup,
        focus,
        setFocus,
        setMax: (key, kg) => who && 'clientId' in who && setMaxM.mutate({ clientId: who.clientId, key, kg }),
        toast,
        unitSel,
        openTypePicker: () => {},
        saveBlockToBase: async (id) => {
          const b = doc.blocks.find((x) => x.id === id);
          if (!b) return;
          const { TYPE_FOLDER } = await import('@tg/domain');
          await saveTpl.mutateAsync({ level: 'block', title: b.title || 'Блок', folder: b.kind ? TYPE_FOLDER[b.kind] : null, kind: b.kind, content: cloneContent(b) });
          utils.trainer.templates.invalidate();
          toast('Блок сохранён в базу');
        },
        aiBlock,
        aiBusy,
        aiLine,
        addOwnExercise: async (name) => {
          const e = await saveEx.mutateAsync({ ru: name, group: 'Без группы', units: ['повт'] });
          toast(`«${name}» добавлено в вашу базу`);
          return e.id;
        },
        nmw,
        results: resQ.data?.results || [],
      }
    : null;

  /* Заголовок страницы с датой — кнопка открывает месяц клиента или группы */
  const [dpk, setDpk] = useState<HTMLElement | null>(null);
  const ro = !!useMe().data?.sub.ended;
  useEffect(() => {
    /* Подписка закончилась — день только смотрят (ReadOnly.tsx): «Тренировка», а не «Создать» */
    const t = ro ? 'Тренировка' : 'Создать тренировку';
    document.title = t + ' · Тренерграм';
    setTitle({
      title: t,
      extra: date ? (
        <button className="ptd" id="ptd" title="Выбрать дату тренировки" onClick={(e) => setDpk(dpk ? null : e.currentTarget)}>
          {longDate(date)}
          <Svg src={ICON.chev} />
        </button>
      ) : null,
    });
  }, [date, dpk, ro]);
  const pickDate = async (d: string) => {
    setDpk(null);
    if (d === date) return;
    await flush.current();
    loadedFor.current = '';
    setProposal(null);
    setSp((p) => (p.set('date', d), p));
  };

  /* Клиентов и групп нет вовсе — а не просто кандидат ещё не подгрузился (NoClients ниже сам отличает одно от другого) */
  /* Телефон: правая панель — шторка снизу, открывается строкой над днём (mobile.css, MOBILE-CHANGELOG) */
  const [railOn, setRailOn] = useMobileRail();
  if (clients.data && groups.data && !clients.data.length && !groups.data.length) return <NoClients />;
  if (!who || !whoItem || !doc || !ctx) return null;

  /* Все блоки удалены — обычный вид с «+ Блок», а не поле новой тренировки (ему нужен первый блок) */
  const fresh = isFresh(doc) && doc.blocks.length > 0 && !proposal;
  const n = dayCount(doc);
  const published = row?.status === 'published';
  const saved = !!row && dayHas(row.content);
  const dirty = !!row?.work || dirtyLocal || pending;
  const aud = isGroup ? 'участники' : 'клиент';
  const see = isGroup ? 'видят' : 'видит';

  return (
    <ConstructorCtx.Provider value={ctx}>
      <div className="cons cwide">
        <main>
          <MRailButton onClick={() => setRailOn(true)} />
          <section className="wk">
            <div className="wkh one">
              <button className="cliSel" title="Сменить клиента или группу" onClick={() => setPicker(() => (w: Who) => setSp((p) => {
                p.delete('client'); p.delete('group');
                p.set('clientId' in w ? 'client' : 'group', 'clientId' in w ? w.clientId : w.groupId);
                loadedFor.current = '';
                return p;
              }))}>
                <Cav ini={whoItem.ini} photo={whoItem.photo} grp={whoItem.kind === 'group'} />
                <span className="cl-t">
                  <b>{whoItem.name}</b>
                  <s>{whoItem.sub}</s>
                </span>
                <Svg src={ICON.chev} />
              </button>
              <div className="dates"></div>
              <div className="views"></div>
            </div>
          </section>
          {whoItem.overLimit ? (
            <section className="doc quick">
              <LimitAlert clientId={'clientId' in who ? who.clientId : undefined} />
            </section>
          ) : fresh ? (
            <section className={'doc quick fresh'} ref={docRef}>
              <div className="doch">
                <TitleInput />
              </div>
              <div className="blk qblk" data-blk={doc.blocks[0].id}>
                {doc.blocks[0].items.slice(0, 1).map((it) =>
                  it.t === 'set' ? null : <LineView key={it.id} it={it as Line} inSet={false} quick />,
                )}
              </div>
              <div className="addbrow">
                <button className="addb" title="Пишете как в заметках — сохранится как написано" onClick={() => update((d) => ({ ...d, blocks: [{ ...mkBlock(), text: '' }] }))}>
                  <Svg src={ICON.text} /> Блок текстом
                </button>
                {photoBtn}
              </div>
            </section>
          ) : (
            <section className="doc quick" ref={docRef}>
              <div className="doch">
                <span className="gr" title="Перетащить тренировку на другой день">
                  <Svg src={ICON.grip} />
                </span>
                {(n > 0 || saved) &&
                  (published ? (
                    <button className="dst pub" data-status="draft" title={`Опубликована — ${aud} ${see}. Нажмите, чтобы перевести в черновик`} onClick={toggle}>
                      <Svg src={DAYICON.pub} />
                      Опубликована
                    </button>
                  ) : (
                    <button className="dst draft" data-status="pub" title={`Черновик — ${aud} не ${see}. Нажмите, чтобы опубликовать`} onClick={toggle}>
                      <Svg src={DAYICON.draft} />
                      Черновик
                    </button>
                  ))}
                <TitleInput />
                <Kebab
                  id="day-menu"
                  title="Действия с тренировкой"
                  items={[
                    {
                      k: 'msg',
                      icon: ICON.chat,
                      label: (doc.message || msgOpen.open ? 'Удалить сообщение ' : 'Сообщение ') + (isGroup ? 'группе' : 'клиенту'),
                      onClick: () => {
                        if (doc.message || msgOpen.open) {
                          if (doc.message) update((d) => ({ ...d, message: undefined }));
                          msgOpen.set(false);
                          return;
                        }
                        msgOpen.set(true);
                        setTimeout(() => document.getElementById('w-msg')?.focus());
                      },
                    },
                    {
                      k: 'comp',
                      icon: DAYICON.comp,
                      label: doc.comp ? 'Снять статус соревнования' : 'Отметить как соревнование',
                      onClick: () => {
                        const on = !doc.comp;
                        update((d) => ({ ...d, comp: on || undefined }));
                        toast(on ? 'День отмечен как соревнование' : 'Статус соревнования снят');
                      },
                    },
                    {
                      k: 'save',
                      icon: ICON.star,
                      label: 'Сохранить в базу тренировок',
                      onClick: async () => {
                        await saveTpl.mutateAsync({ level: 'workout', title: doc.title || 'Тренировка', content: cloneContent(doc) });
                        for (const b of doc.blocks) if (b.title || b.kind) await saveTpl.mutateAsync({ level: 'block', title: b.title || 'Блок', kind: b.kind, content: cloneContent(b) });
                        utils.trainer.templates.invalidate();
                        toast('Тренировка сохранена в базу — вместе с блоками');
                      },
                    },
                    doc.blocks.length > 0 && {
                      k: 'clear',
                      icon: ICON.erase,
                      label: 'Очистить день',
                      cls: 'rm',
                      onClick: () => {
                        const before = doc;
                        setDoc(freshDoc());
                        toast('День очищен — сохраните, чтобы убрать из календаря', () => setDoc(before));
                      },
                    },
                  ]}
                />
              </div>
              <MsgField />
              {doc.blocks.map((b, k) => (
                <div key={b.id} style={{ display: 'contents' }}>
                  {proposal && proposal.ids.has(b.id) && doc.blocks.findIndex((x) => proposal.ids.has(x.id)) === k && (
                    <div className="propose">
                      <span className="t">
                        <b>
                          <Svg src={'<svg class="aim" viewBox="0 0 16 16" fill="currentColor"><path d="M10.2 3.4Q11.2 8 15.8 9Q11.2 10 10.2 14.6Q9.2 10 4.6 9Q9.2 8 10.2 3.4Z"/></svg>'} /> Разобрано ИИ — проверьте
                        </b>
                        <s>{proposalStat()}</s>
                      </span>
                      <button className="lnk" onClick={() => toast(proposal.source.text || '')}>
                        Исходный текст
                      </button>
                      <button
                        className="btn gh"
                        onClick={() => {
                          const src = proposal.source;
                          update((d) => {
                            const ids = proposal.ids;
                            const at = d.blocks.findIndex((x) => ids.has(x.id));
                            const rest = d.blocks.filter((x) => !ids.has(x.id));
                            rest.splice(at < 0 ? proposal.at : at, 0, src);
                            return { ...d, blocks: rest };
                          });
                          closeProposal('reverted');
                        }}
                      >
                        Вернуть текст
                      </button>
                      <button className="btn" onClick={() => closeProposal('accepted')}>
                        Принять
                      </button>
                    </div>
                  )}
                  <BlockView b={b} k={k} quick pending={!!proposal?.ids.has(b.id)} />
                </div>
              ))}
              <div className="addbrow">
                <button className="addb" id="add-blk" onClick={() => {
                  let id = '';
                  update((d) => {
                    const nd = M.addBlock(d);
                    const b = nd.blocks[nd.blocks.length - 1];
                    id = (b.items[0] as Line)?.id;
                    return nd;
                  });
                  setTimeout(() => id && setFocus(id, 'e'));
                }}>
                  <Svg src={ICON.plus} /> Блок
                </button>
                <button className="addb" id="add-blk-text" title="Пишете как в заметках — сохранится как написано" onClick={() => update((d) => M.addBlock(d, undefined, true))}>
                  <Svg src={ICON.text} /> Блок текстом
                </button>
                {photoBtn}
              </div>
              <div className="pubbar">
                <button
                  className={'pubsw' + (published ? ' on' : '')}
                  role="switch"
                  aria-checked={published}
                  data-status={published ? 'draft' : 'pub'}
                  disabled={!(n > 0 || saved)}
                  title={!(n > 0 || saved) ? 'Пустой день опубликовать нечего' : published ? `Опубликована — ${aud} ${see} сохранённую версию. Выключите, чтобы скрыть` : `Черновик — ${aud} не ${see}. Включите, чтобы опубликовать`}
                  onClick={toggle}
                >
                  Опубликовать<i></i>
                </button>
                <button className="btn" id="saveAll" disabled={!dirty} title={`Сохранить изменения · ${SAVE_KEY}`} onClick={save}>
                  <Svg src={ICON.chk} /> Сохранить
                </button>
              </div>
            </section>
          )}
        </main>
        <MRailBackdrop onClose={() => setRailOn(false)} />
        <Rail
          onCloseSheet={() => setRailOn(false)}
          who={who}
          whoLabel={{ ini: whoItem.ini, name: whoItem.name, photo: whoItem.photo }}
          date={date}
          onPickWho={(cb) => setPicker(() => cb)}
          onCopyDay={(c) => {
            const before = doc;
            setDoc(c);
            setRailOn(false);
            toast('Тренировка скопирована', () => setDoc(before));
          }}
          onAddBlock={(b) => update((d) => M.insertBlocks(d, [b]))}
          onAddLine={(l) =>
            update((d) => {
              const last = [...d.blocks].reverse().find((b) => typeof b.text !== 'string');
              if (!last) return M.insertBlocks(d, [mkBlock({ items: [l] })]);
              return M.edit(d, (x) => {
                const b = x.blocks.find((y) => y.id === last.id)!;
                const blankAt = b.items.findIndex(M.isBlankLine);
                if (blankAt >= 0) b.items.splice(blankAt, 1, l as any);
                else b.items.push(l as any);
              });
            })
          }
          onAddExercise={(exId) =>
            update((d) => {
              const l: Line = { ...mkLine(exId) };
              const last = [...d.blocks].reverse().find((b) => typeof b.text !== 'string');
              if (!last) return M.insertBlocks(d, [mkBlock({ items: [l] })]);
              return M.edit(d, (x) => {
                const b = x.blocks.find((y) => y.id === last.id)!;
                const blankAt = b.items.findIndex(M.isBlankLine);
                if (blankAt >= 0) b.items.splice(blankAt, 1, l as any);
                else b.items.push(l as any);
              });
            })
          }
        />
      </div>
      {picker && (
        <WhoPicker
          curId={who ? ('groupId' in who ? who.groupId : who.clientId) : null}
          clients={(clients.data || []).map((c) => ({ kind: 'client' as const, id: c.id, name: c.name, ini: ini(c.name), photo: c.photo, sub: c.group ? 'группа ' + c.group.name : '' }))}
          groups={(groups.data || []).map((g) => ({ kind: 'group' as const, id: g.id, name: g.name, ini: ini(g.name), sub: `${g.members.length} участников` }))}
          onPick={(w) => {
            picker(w);
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
      {conflicts && (
        <GroupConflicts
          list={conflicts.list}
          names={Object.fromEntries((clients.data || []).map((c) => [c.id, c.name]))}
          onDone={(d) => {
            conflicts.resolve(d);
            setConflicts(null);
          }}
        />
      )}
      {dpk && who && <DatePick who={who} date={date} anchor={dpk} onPick={pickDate} onClose={() => setDpk(null)} />}
      {toastEl}
    </ConstructorCtx.Provider>
  );
}

function TitleInput() {
  const c = useCtxSafe();
  const [v, setV] = useState(c.doc.title);
  useEffect(() => setV(c.doc.title), [c.doc.title]);
  return (
    <input
      id="d-title"
      value={v}
      placeholder="Новая тренировка"
      onChange={(e) => setV(e.target.value)}
      onBlur={() => v !== c.doc.title && c.update((d) => ({ ...d, title: v }))}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
    />
  );
}

/* Сообщение ко всей тренировке (COM-4) */
/* Поле «Сообщение клиенту/группе» открывается из меню «⋯» тренировки; пустое закрывается само */
const msgOpen = { open: false, set: (v: boolean) => void ((msgOpen.open = v), window.dispatchEvent(new Event('tg-msg'))) };
function MsgField() {
  const c = useCtxSafe();
  const [open, setOpen] = useState(false);
  const [v, setV] = useState(c.doc.message || '');
  useEffect(() => setV(c.doc.message || ''), [c.doc.message]);
  useEffect(() => {
    const f = () => setOpen(msgOpen.open);
    window.addEventListener('tg-msg', f);
    return () => window.removeEventListener('tg-msg', f);
  }, []);
  if (!c.doc.message && !open) return null;
  return (
    <label className="fld wmsg">
      <span className="k">
        <Svg src={ICON.chat} /> {c.isGroup ? 'Группе' : 'Клиенту'}
      </span>
      <input
        id="w-msg"
        value={v}
        placeholder={c.isGroup ? 'Сообщение ко всей тренировке — каждый участник увидит его первым' : 'Сообщение ко всей тренировке — клиент увидит его первым'}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => {
          if (v !== (c.doc.message || '')) c.update((d) => ({ ...d, message: v || undefined }));
          if (!v) msgOpen.set(false);
        }}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      />
      <kbd className="ent">↵ Enter</kbd>
      <button className="x" id="w-msgdel" title="Удалить сообщение" onClick={() => {
        setV('');
        msgOpen.set(false);
        c.update((d) => ({ ...d, message: undefined }));
      }}>
        <Svg src={ICON.x} />
      </button>
    </label>
  );
}

/* Свежий тренер без единого клиента и группы — составлять ещё некому.
   Тон сообщения — как у «В группе пока никого» (grpPanelHTML в прототипе). */
function NoClients() {
  return (
    <div className="cons cwide">
      <main>
        <section className="doc quick">
          <div className="doc-empty">
            <div className="t">У вас пока нет клиентов</div>
            <div className="d">Отправьте ссылку-приглашение — конструктор откроется, как только появится первый клиент</div>
            <InviteLink />
          </div>
        </section>
      </main>
    </div>
  );
}

import { useCtx as useCtxSafe } from './ctx';
import { Cav } from '../ui/Cav';
export const ini = (n: string) => {
  const w = String(n || '').trim().split(/[^а-яёa-z0-9]+/i).filter(Boolean);
  return (w.length === 1 && w[0].length <= 3 ? w[0] : w.length > 1 ? w.slice(0, 2).map((x) => x[0]).join('') : (w[0] || '?').slice(0, 2)).toUpperCase();
};
export const _api = api;

/** Фото → JPEG не больше 2000 px по длинной стороне (лимит запроса к API — 5 МБ, модели больше не нужно) */
async function shrinkImage(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((ok, fail) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = () => fail(new Error('Не удалось открыть картинку'));
      i.src = url;
    });
    const k = Math.min(1, 2000 / Math.max(img.naturalWidth, img.naturalHeight));
    const cv = document.createElement('canvas');
    cv.width = Math.round(img.naturalWidth * k);
    cv.height = Math.round(img.naturalHeight * k);
    const g = cv.getContext('2d')!;
    g.fillStyle = '#fff';
    g.fillRect(0, 0, cv.width, cv.height);
    g.drawImage(img, 0, 0, cv.width, cv.height);
    return cv.toDataURL('image/jpeg', 0.85);
  } finally {
    URL.revokeObjectURL(url);
  }
}
